/**
 * Shared frame for the three legal pages (Privacy, Terms, Cookies).
 *
 * Kept intentionally lightweight — public, unauthenticated, dark marketing
 * palette to match the Landing page rather than the app shell. Uses the
 * canonical Brand mark and BrandName wordmark so casing stays consistent.
 */

import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Brand from "@/components/site/Brand";
import BrandName from "@/components/site/BrandName";
import Footer from "@/components/site/Footer";

export default function LegalLayout({
  kicker,
  title,
  lastUpdated,
  intro,
  children,
  testid,
}) {
  return (
    <div
      data-testid={testid}
      className="min-h-screen bg-[#040914] text-slate-100 overflow-x-hidden"
    >
      <header className="border-b border-white/[0.06] bg-[#050B18]/85 backdrop-blur">
        <div className="mx-auto max-w-5xl px-5 lg:px-8 h-16 flex items-center justify-between">
          <Brand />
          <Link
            to="/"
            data-testid="legal-back-home"
            className="inline-flex items-center gap-1.5 text-[12px] uppercase tracking-[0.18em] text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft size={12} /> Back to home
          </Link>
        </div>
      </header>

      <section className="mx-auto max-w-3xl px-5 lg:px-8 pt-16 pb-20">
        <div className="text-[11px] uppercase tracking-[0.22em] text-[#D4A65A] font-semibold">
          {kicker}
        </div>
        <h1 className="mt-3 font-display text-4xl md:text-5xl tracking-tight text-white font-semibold">
          {title}
        </h1>
        <div className="mt-3 text-[12.5px] text-slate-500 font-mono">
          Last updated · {lastUpdated}
        </div>
        {intro && (
          <p className="mt-6 text-[15px] leading-relaxed text-slate-300">
            {intro}
          </p>
        )}
        <div className="mt-10 space-y-10 text-[14.5px] leading-relaxed text-slate-300">
          {children}
        </div>

        <div className="mt-16 rounded-xl border border-white/[0.08] bg-gradient-to-r from-[#0B1424] via-[#080F1E] to-[#0B1424] p-5 md:p-6">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4A65A] font-semibold">
            Questions about this policy?
          </div>
          <p className="mt-2 text-[13.5px] text-slate-300">
            Email our data-protection team at{" "}
            <a
              href="mailto:privacy@ukcsl.co.uk"
              className="text-white underline underline-offset-4 decoration-[#D4A65A]/40 hover:decoration-[#D4A65A]"
              data-testid="legal-contact-email"
            >
              privacy@ukcsl.co.uk
            </a>
            . We respond within five working days.
          </p>
        </div>

        <div className="mt-10 text-[12.5px] text-slate-500">
          <BrandName /> is operated by UKCSL Ltd (United Kingdom) in
          collaboration with Aidoido S.L. (Spain, EU).
        </div>
      </section>

      <Footer />
    </div>
  );
}

/* ----------------------- section helpers ----------------------- */

export function LegalSection({ number, title, children, testid }) {
  return (
    <section data-testid={testid} className="scroll-mt-24">
      <div className="flex items-baseline gap-3">
        <span className="text-[11px] font-mono text-[#D4A65A] font-semibold">
          {number}
        </span>
        <h2 className="font-display text-xl md:text-2xl text-white font-semibold tracking-tight">
          {title}
        </h2>
      </div>
      <div className="mt-4 space-y-3 text-slate-300">{children}</div>
    </section>
  );
}

export function LegalList({ items }) {
  return (
    <ul className="space-y-2 list-disc pl-5 marker:text-[#D4A65A]/70">
      {items.map((it, i) => (
        <li key={i}>{it}</li>
      ))}
    </ul>
  );
}
